<template>
  <wa-force-update-on-mounted>
    <header
      id="header"
      :class="componentClass"
      :style="componentStyle"
    >
      <div class="container">
        <div class="wrap-app-header position-relative display-flex flex-items-center flex-content-between size-lg tablet:size-md">
          <div class="app-header-menu-toggle display-flex flex-items-center tablet:display-none">
            <button
              class="flat swap-icons"
              :aria-label="mainMenuLabel"
              data-balloon-pos="right"
              @click="toggleMainMenu"
            >
              <font-awesome-icon
                :icon="isShowingMainMenu ? $v7.regular.faXmark : $v7.regular.faBars"
                size="xl"
                class="initial-icon"
              />
              <font-awesome-icon
                :icon="$v7.regular.faHamburger"
                size="xl"
                class="hover-icon"
              />
              <span class="sr-only">{{ mainMenuLabel }}</span>
            </button>
          </div>

          <div class="app-header-links display-flex flex-items-center tablet:flex-items-stretch flex-content-center tablet:flex-content-start">
            <site-link
              :to="{ name: 'home' }"
              class="wrapper-logo-flag-animated button flat"
              @click.native="$posthog.capture('main_navigation_link_click', { destination: 'home' })"
            >
              <wa-logo-flag :logo-color="logoColor || undefined" />
              <span class="sr-only">Font Awesome</span>
            </site-link>

            <div class="display-none tablet:display-block tablet:margin-left-3xl">
              <div
                class="wrap-links display-flex flex-items-center"
                style="gap: var(--spacing-md)"
              >
                <site-link
                  v-if="$account"
                  :to="routes.kits.location"
                  :class="mainLink(routes.kits)"
                  @click.native="$posthog.capture('main_navigation_link_click', { destination: 'kits' })"
                >
                  <span class="display-none laptop:display-inline">Your </span>Kits
                </site-link>
                <site-link
                  v-else
                  :to="routes.start.location"
                  :class="mainLink(routes.start)"
                  @click.native="$posthog.capture('main_navigation_link_click', { destination: 'start' })"
                >
                  Start
                </site-link>

                <site-link
                  :to="routes.search.location"
                  :class="mainLink(routes.search)"
                  title="Search icons"
                  @click.native="$posthog.capture('main_navigation_link_click', { destination: 'icon_search' })"
                >
                  <font-awesome-icon :icon="$v7.solid.faMagnifyingGlass" />
                  <span class="sr-only">Search Icons</span>
                </site-link>

                <site-link
                  :to="routes.icons.location"
                  :class="mainLink(routes.icons)"
                  @click.native="$posthog.capture('main_navigation_link_click', { destination: 'icons' })"
                >
                  Icons
                </site-link>

                <site-link
                  :to="routes.docs.location"
                  :class="mainLink(routes.docs)"
                  @click.native="$posthog.capture('main_navigation_link_click', { destination: 'docs' })"
                >
                  Docs
                </site-link>

                <site-link
                  :to="routes.plans.location"
                  :class="mainLink(routes.plans)"
                  @click.native="$posthog.capture('main_navigation_link_click', { destination: 'plans' })"
                >
                  Plans
                </site-link>

                <site-link
                  :to="routes.support.location"
                  :class="mainLink(routes.support)"
                  @click.native="$posthog.capture('main_navigation_link_click', { destination: 'support' })"
                >
                  Support
                </site-link>

                <a
                  href="https://podcastawesome.com"
                  target="_blank"
                  class="no-underline padding-y-xs padding-x-md margin-right-md"
                  @click="$posthog.capture('main_navigation_link_click', { destination: 'podcast' })"
                >
                  Podcast
                </a>
              </div>
            </div>
          </div>

          <div class="app-header-account display-flex flex-items-center tablet:margin-left-2xl">
            <site-link
              v-if="$account"
              :to="routes.account.location"
              :class="['button flat', { active: routes.account.active }]"
              aria-label="Your Account"
              data-balloon-pos="left"
              @click.native="$posthog.capture('main_navigation_link_click', { destination: 'account' })"
            >
              <font-awesome-icon
                :icon="$v7.regular.faCircleUser"
                size="xl"
              />
              <span class="sr-only">Your Account</span>
            </site-link>
            <site-link
              v-else
              :to="routes.signIn.location"
              :class="['button flat swap-icons', { active: routes.signIn.active }]"
              aria-label="Sign In"
              data-balloon-pos="left"
              @click.native="$posthog.capture('main_navigation_link_click', { destination: 'sign_in' })"
            >
              <font-awesome-icon
                :icon="$v7.regular.faRightToBracket"
                size="xl"
                class="initial-icon"
              />
              <font-awesome-icon
                :icon="$v7.regular.faPersonToPortal"
                size="xl"
                class="hover-icon"
              />
              <span class="sr-only">Sign In</span>
            </site-link>
          </div>
        </div>

        <transition
          name="toggle-app-header-menu"
          enter-active-class="animate__animated animate__faster animate__fadeIn"
          leave-active-class="animate__animated animate__faster animate__fadeOut"
          appear
        >
          <div
            v-if="isShowingMainMenu"
            class="app-header-menu with-border-bottom tablet:display-none"
          >
            <div
              class="wrap-links padding-x-md"
              style="--hr-border-color: var(--fa-lt-gravy)"
            >
              <site-link
                v-if="$account"
                :to="routes.kits.location"
                :class="menuLink(routes.kits)"
                @click.native="$posthog.capture('main_navigation_link_click', { destination: 'kits', context: 'responsive_menu' })"
              >
                Your Kits
              </site-link>
              <site-link
                v-else
                :to="routes.start.location"
                :class="menuLink(routes.start)"
                @click.native="$posthog.capture('main_navigation_link_click', { destination: 'start', context: 'responsive_menu' })"
              >
                Start
              </site-link>

              <site-link
                :to="routes.search.location"
                :class="menuLink(routes.search)"
                @click.native="$posthog.capture('main_navigation_link_click', { destination: 'icon_search', context: 'responsive_menu' })"
              >
                Search
              </site-link>

              <site-link
                :to="routes.icons.location"
                :class="menuLink(routes.icons)"
                @click.native="$posthog.capture('main_navigation_link_click', { destination: 'icons', context: 'responsive_menu' })"
              >
                Icons
              </site-link>

              <site-link
                :to="routes.docs.location"
                :class="menuLink(routes.docs)"
                @click.native="$posthog.capture('main_navigation_link_click', { destination: 'docs', context: 'responsive_menu' })"
              >
                Docs
              </site-link>

              <site-link
                :to="routes.plans.location"
                :class="menuLink(routes.plans)"
                @click.native="$posthog.capture('main_navigation_link_click', { destination: 'plans', context: 'responsive_menu' })"
              >
                Plans
              </site-link>

              <site-link
                :to="routes.support.location"
                :class="menuLink(routes.support)"
                @click.native="$posthog.capture('main_navigation_link_click', { destination: 'support', context: 'responsive_menu' })"
              >
                Support
              </site-link>

              <a
                href="https://podcastawesome.com"
                target="_blank"
                class="display-block no-underline padding-y-sm padding-x-md"
                @click="$posthog.capture('main_navigation_link_click', { destination: 'podcast', context: 'responsive_menu' })"
              >
                Podcast
              </a>
            </div>
          </div>
        </transition>
      </div>
    </header>
  </wa-force-update-on-mounted>
</template>

<script>
  import { PageHeaderRoutes } from './mixins'

  export default {
    name: 'PageHeader',

    mixins: [PageHeaderRoutes],

    props: {
      customStyle: {
        type: Object,
        default: () => {}
      },
      isInverted: {
        type: Boolean,
        default: false
      },
      themeColor: {
        type: String
      }
    },

    computed: {
      componentClass() {
        return ['app-header padding-y-md', { inverted: this.isInverted }]
      },

      componentStyle() {
        let style = {}

        style['--button-background'] = 'transparent'
        style['--button-hover-background'] = 'transparent'
        style['--button-margin-bottom'] = '0'
        style['--button-padding'] = 'var(--spacing-2xs) var(--spacing-md)'

        if (this.isInverted) {
          style['--button-color'] = 'var(--fa-md-gravy)'
          style['--button-active-color'] = 'var(--fa-dk-blue)'
          style['--button-hover-color'] = 'var(--fa-dk-blue)'
          style['--link-color'] = 'var(--fa-lt-gravy)'
          style['--link-hover-color'] = 'var(--fa-blue)'
          style['--link-active-color'] = 'var(--white)'
        }

        if (!this.isInverted) {
          style['background'] = 'var(--white)'
          style['--button-color'] = 'var(--fa-md-gravy)'
          style['--button-active-color'] = 'var(--fa-dk-blue)'
          style['--button-hover-color'] = 'var(--fa-dk-blue)'
          style['--link-color'] = 'var(--fa-md-gravy)'
          style['--link-hover-color'] = 'var(--fa-dk-blue)'
          style['--link-active-color'] = 'var(--fa-navy)'
        }

        if (this.themeColor) {
          style['--link-active-color'] = this.themeColor
        }

        return {
          ...style,
          ...this.customStyle
        }
      },

      logoColor() {
        return this.themeColor || null
      }
    },

    methods: {
      mainLink(route) {
        return ['no-underline padding-y-xs padding-x-md', { active: route.active }]
      },

      menuLink(route) {
        return ['display-block with-border-bottom no-underline padding-y-sm padding-x-md', { active: route.active }]
      }
    }
  }
</script>
